// externals
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// interstate
import { Box } from '@interstate/components/Box';
import { CheckBox } from '@interstate/components/CheckBox';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { Typography } from '@interstate/components/Typography';

// utils
import { kebabCase } from '@makemydeal/dr-common-utils';

// store
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as paymentSelectors from '@makemydeal/dr-dash-store/selectors/payment';
import { dealLockHooks } from '@makemydeal/dr-dash-components';

// redux
import { actionCreators, selectors } from '@makemydeal/dr-offer-redux';

// types
import type { paymentServicesTypes } from '@makemydeal/dr-platform-types';

// constants
import { UPFRONT, UPFRONT_TAXES_CATEGORY } from '../constants';

// styles
import { TaxCategoryHeading } from './TaxCategory.style';

// components
import TaxItemSummary from './TaxItemSummary';

interface TaxCategorySectionProps {
    categoryTitle: string;
    taxItems: paymentServicesTypes.TaxItem[];
    dataTestId: string;
    canToggleUpfrontAllTaxes?: boolean;
    hideTaxNames?: boolean;
    taxOverrideEnabled?: boolean;
}

const TaxCategory: React.FC<TaxCategorySectionProps> = ({
    categoryTitle,
    taxItems,
    dataTestId,
    canToggleUpfrontAllTaxes,
    hideTaxNames,
    taxOverrideEnabled
}) => {
    const theme = useInterstateTheme();
    const dispatch = useDispatch();
    const isLocked = dealLockHooks.useIsDealLocked();
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const isPaymentCalculating = useSelector(paymentSelectors.isCalculatingPayment);
    const taxItemsWithOverridesWithoutFlat = useSelector(offerReduxSelectors.getTaxItemsWithOverridesWithoutFlat);
    const isCheckedUpfrontAllTaxes = categoryTitle === UPFRONT_TAXES_CATEGORY && canToggleUpfrontAllTaxes;

    const toggleUpfrontAllTaxes = useCallback(() => {
        if (taxItems?.length) {
            const categoryTaxNames = new Set(taxItems.map((taxItem) => taxItem.taxName));
            const categoryTaxItemsWithOverridesWithoutFlat = taxItemsWithOverridesWithoutFlat.filter((taxItem) =>
                categoryTaxNames.has(taxItem.taxName)
            );

            dispatch(
                actionCreators.updateTaxItems(
                    selectors.capitalizeTaxItems(categoryTaxItemsWithOverridesWithoutFlat, isCheckedUpfrontAllTaxes),
                    offerType,
                    false
                )
            );
        }

        /** NOTE: updateTaxItem's isCapitalize is stateless and PO response will flush it,
         *  so we need to use `updatedTotalTaxUpfrontOverride` to preserve the choice globally
         */
        dispatch(offerActionCreators.updatedTotalTaxUpfrontOverride(!isCheckedUpfrontAllTaxes));
    }, [dispatch, offerType, taxItems, taxItemsWithOverridesWithoutFlat, isCheckedUpfrontAllTaxes]);

    return (
        <Box data-testid={dataTestId} marginTop={1}>
            <TaxCategoryHeading theme={theme}>
                <div>
                    <Typography variant="h5">{categoryTitle}</Typography>
                </div>
                {canToggleUpfrontAllTaxes && (
                    <CheckBox
                        sx={{
                            backgroundColor: 'inherit'
                        }}
                        data-testid={`${dataTestId}-upfront-checkbox`}
                        id={kebabCase(UPFRONT)}
                        disabled={isPaymentCalculating || isLocked}
                        label={'Upfront All Taxes'}
                        name={kebabCase(UPFRONT)}
                        onChange={toggleUpfrontAllTaxes}
                        checked={isCheckedUpfrontAllTaxes}
                        value="1"
                    />
                )}
            </TaxCategoryHeading>
            <Box>
                {taxItems?.length ? (
                    taxItems.map((taxItem, i) => (
                        <TaxItemSummary
                            data-testid={taxItem.taxName}
                            taxItem={taxItem}
                            key={taxItem.taxName + i}
                            hideTaxName={hideTaxNames}
                            disabledCrtUpfrontCheckbox={isCheckedUpfrontAllTaxes || isPaymentCalculating}
                            taxOverrideEnabled={taxOverrideEnabled}
                        />
                    ))
                ) : (
                    <Typography variant="body-sm" sx={{ marginBottom: 3, paddingLeft: 2, paddingRight: 2 }}>
                        N/A
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default TaxCategory;
